import { useState } from 'react'
import { Modal, Button, Table, Spinner, Badge, Accordion } from 'react-bootstrap'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_TWILIO_SYNC_PREVIEW } from '../../gql/queries/twilio'
import { APPLY_SYNC_TWILIO_TEMPLATES } from '../../gql/mutations/twilio'
import { KTIcon } from '../../../../_metronic/helpers'

export const TwilioSyncDiffModal = ({
  show,
  onHide,
  onSuccess
}: {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}) => {
  const [selectedSids, setSelectedSids] = useState<Set<string>>(new Set())

  // Fetch the diff (preview sync without saving)
  const { data, loading, error, refetch } = useQuery(GET_TWILIO_SYNC_PREVIEW, {
    skip: !show,
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      // Auto-select all new and changed templates by default
      const sids = new Set<string>()
      d.twilioSyncPreview?.newTemplates.forEach((t: any) => sids.add(t.contentSid))
      d.twilioSyncPreview?.changedTemplates.forEach((t: any) => sids.add(t.contentSid))
      setSelectedSids(sids)
    }
  })

  // Apply the sync
  const [applySync, { loading: applying }] = useMutation(APPLY_SYNC_TWILIO_TEMPLATES)

  const diff = data?.twilioSyncPreview

  const handleToggleSelect = (sid: string) => {
    const next = new Set(selectedSids)
    if (next.has(sid)) next.delete(sid)
    else next.add(sid)
    setSelectedSids(next)
  }

  const handleApply = async () => {
    if (selectedSids.size === 0) {
      toast.warning('Debe seleccionar al menos una plantilla para sincronizar.')
      return
    }

    try {
      const { data: res } = await applySync({
        variables: { acceptedSids: Array.from(selectedSids) }
      })

      if (res?.applySyncTwilioTemplates?.success) {
        toast.success(res.applySyncTwilioTemplates.message)
        onSuccess()
        onHide()
      } else {
        toast.error(res?.applySyncTwilioTemplates?.message || 'Failed to apply sync')
      }
    } catch (e: any) {
      toast.error(e.message || 'Critical error applying sync')
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Twilio Templates Sync (Diff Preview)</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2 text-muted">Querying Twilio Content API...</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <strong>Error fetching data:</strong> {error.message}
            <div className="mt-3">
              <Button size="sm" variant="danger" onClick={() => refetch()}>Retry</Button>
            </div>
          </div>
        ) : diff ? (
          <div>
            <div className="d-flex justify-content-between mb-4">
              <div>
                <h5>Summary</h5>
                <span className="badge badge-light-success fw-bolder px-4 py-3 me-2">New: {diff.newTemplates.length}</span>
                <span className="badge badge-light-warning fw-bolder px-4 py-3 me-2">Modified: {diff.changedTemplates.length}</span>
                <span className="badge badge-light-secondary fw-bolder px-4 py-3 me-2">Unchanged: {diff.unchangedCount}</span>
                <span className="badge badge-light-primary fw-bolder px-4 py-3">Total Remote: {diff.totalRemote}</span>
              </div>
              <div className="text-end text-muted small pt-4">
                Selected to save: {selectedSids.size}
              </div>
            </div>

            {/* Nuevas Plantillas */}
            {diff.newTemplates.length > 0 && (
              <div className="mb-5">
                <h6 className="text-success fw-bolder mb-3">
                  <KTIcon iconName="plus" className="fs-3 text-success me-1" />
                  New Templates Detected
                </h6>
                <Table hover responsive className="align-middle border rounded">
                  <thead className="table-light">
                    <tr>
                      <th className="w-50px text-center">
                        <i className="bi bi-check2-square text-muted"></i>
                      </th>
                      <th>SID / Name</th>
                      <th>Status</th>
                      <th>Language</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.newTemplates.map((t: any) => (
                      <tr key={`new-${t.contentSid}`}>
                        <td className="text-center">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            checked={selectedSids.has(t.contentSid)}
                            onChange={() => handleToggleSelect(t.contentSid)}
                          />
                        </td>
                        <td>
                          <div className="fw-bolder">{t.friendlyName}</div>
                          <div className="text-muted small"><code>{t.contentSid}</code></div>
                        </td>
                        <td><Badge bg={t.approvalStatus === 'approved' ? 'success' : 'warning'}>{t.approvalStatus}</Badge></td>
                        <td>{t.language}</td>
                        <td><span className="badge badge-light-success">Will be created locally</span></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Plantillas Modificadas */}
            {diff.changedTemplates.length > 0 && (
              <div className="mb-5">
                <h6 className="text-warning fw-bolder mb-3">
                  <KTIcon iconName="information-5" className="fs-3 text-warning me-1" />
                  Templates with Differences
                </h6>
                <Accordion>
                  {diff.changedTemplates.map((t: any, index: number) => {
                    const local = t.localSnapshot
                    const remote = t.remoteSnapshot
                    return (
                      <Accordion.Item eventKey={index.toString()} key={`changed-${t.contentSid}`}>
                        <Accordion.Header>
                          <div className="d-flex align-items-center w-100">
                            <input 
                                className="form-check-input me-3" 
                                type="checkbox" 
                                checked={selectedSids.has(t.contentSid)}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  handleToggleSelect(t.contentSid)
                                }}
                            />
                            <div className="flex-grow-1">
                              <span className="fw-bolder me-2">{t.friendlyName}</span>
                              <span className="badge badge-light-danger me-2">Modified Fields: {t.diffFields.length}</span>
                              <code className="text-muted small">{t.contentSid}</code>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Field</th>
                                <th className="text-danger">Current Local Version</th>
                                <th className="text-success">New Version (Twilio)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.diffFields.map((field: string) => (
                                <tr key={field}>
                                  <td className="fw-bold">{field}</td>
                                  <td className="text-danger">
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                                      {typeof local[field] === 'object' ? JSON.stringify(local[field], null, 2) : String(local[field] ?? 'null')}
                                    </pre>
                                  </td>
                                  <td className="text-success">
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                                      {typeof remote[field] === 'object' ? JSON.stringify(remote[field], null, 2) : String(remote[field] ?? 'null')}
                                    </pre>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          <div className="mt-3 text-muted small">
                            <i className="bi bi-info-circle me-1"></i>
                            By saving, the local version will be replaced with Twilio's data.
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    )
                  })}
                </Accordion>
              </div>
            )}

            {diff.newTemplates.length === 0 && diff.changedTemplates.length === 0 && (
              <div className="text-center py-5">
                <i className="bi bi-check-circle text-success fs-1 mb-3 d-block"></i>
                <h5>Everything is up to date!</h5>
                <p className="text-muted">There are no differences between Twilio templates and your local database.</p>
              </div>
            )}

          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={applying}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApply} 
          disabled={loading || applying || selectedSids.size === 0 || !diff}
        >
          {applying ? (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Saving {selectedSids.size} templates... <Spinner animation="border" size="sm" className="ms-2 align-middle" />
            </span>
          ) : (
            `Save (${selectedSids.size}) Selected`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
